import { useState, useRef, useEffect } from "react";
import dynamic from "next/dynamic";
import { useController, useForm, useFormContext } from "react-hook-form";
const SearchBox = dynamic(
  () => import("@mapbox/search-js-react").then((mod) => mod.SearchBox),
  { ssr: false }
);
import styles from "./MapboxSearch.module.scss";
import { hasNonLatinCharacters } from "utils/helpers/language";

const formatPlaceValue = (value) => {
  if (!value) return "";

  const { name, place } = value;
  if (!name && !place) return "";

  return `${name ? name : ""}${place ? ", " + place : ""}`;
};

// Only for use with react-hook-form

export default function MapboxSearch({
  id,
  onChange = () => {},
  currentValue,
  control,
}) {
  const [input, setInput] = useState("");
  const [value, setValue] = useState(currentValue);
  const [currentSuggestions, setCurrentSuggestions] = useState([]);
  const searchBoxRef = useRef(null);

  const { field } = useController({
    name: id,
    control,
    rules: { required: true },
  });

  const { trigger, setError } = useFormContext();

  const handleSearchBlur = () => {
    field.onBlur();

    if (!value?.country?.name && !value?.name) {
      setValue(null);
    }

    if (!value) {
      setError(id, { type: "required", message: "Please select a location" });
    }
  };

  const handleSearchChange = (searchValue) => {
    if (searchValue === "" || !searchValue || searchValue.length === 0) {
      onChange(id, null);
      field.onChange(null);
      setValue(null);
      setInput(null);
      trigger(id);
    }

    setInput(searchValue);
  };

  const handleSearchSuggest = ({ suggestions }) => {
    setCurrentSuggestions(suggestions);
  };

  const handleSearchRetrieve = (result) => {
    console.log(result);
    const properties = result?.features[0]?.properties;

    const {
      bbox,
      name,
      place_formatted: place,
      context,
      coordinates,
      feature_type,
      mapbox_id,
    } = properties;

    let placeName = name;
    const placeNameIsNonLatin = hasNonLatinCharacters(placeName);

    // if (placeNameIsNonLatin) {
    const matchingSuggestion = currentSuggestions.find(
      (suggestion) => suggestion.mapbox_id === mapbox_id
    );

    placeName = matchingSuggestion?.name;

    // }

    let countryData = context?.country;

    if (!countryData?.name) {
      const matchingSuggestion = currentSuggestions.find(
        (suggestion) => suggestion.mapbox_id === mapbox_id
      );

      countryData = matchingSuggestion?.context?.country;
    }

    const placeData = {
      bbox,
      coordinates,
      country: countryData,
      name: placeName,
      feature_type,
      formatted_place: formatPlaceValue({ name, place }),
      region: context?.region ?? {},
    };

    !!onChange ? onChange(placeData) : "";
    setValue(placeData);

    field.onChange(placeData);

    trigger(id);
  };

  return (
    <MapboxSearchBox
      id={id}
      value={value?.formatted_place ?? currentValue?.formatted_place ?? input}
      handleSearchSuggest={handleSearchSuggest}
      handleSearchChange={handleSearchChange}
      handleSearchRetrieve={handleSearchRetrieve}
      handleSearchBlur={handleSearchBlur}
      searchBoxRef={searchBoxRef}
    />
  );
}

export function MapboxSearchBox({
  id,
  value,
  handleSearchChange,
  handleSearchSuggest,
  handleSearchRetrieve,
  handleSearchBlur,
  searchBoxRef,
}) {
  const [eventListenerSet, setEventListenerSet] = useState(false);

  useEffect(() => {
    if (!!searchBoxRef.current) {
      const input = searchBoxRef.current.querySelector("input");

      if (!!input && !eventListenerSet) {
        input.addEventListener("blur", handleSearchBlur);
        setEventListenerSet(true);
      }
    }
  }, [searchBoxRef, handleSearchBlur]);

  return (
    <div className={`${styles.container}`} ref={searchBoxRef}>
      <SearchBox
        accessToken={process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN}
        id={id}
        placeholder="Search for a country, region, city, or town"
        onChange={handleSearchChange}
        onRetrieve={handleSearchRetrieve}
        onSuggest={handleSearchSuggest}
        value={value}
        limit={8}
        options={{
          types: [
            "country",
            "region",
            "district",
            "place",
            "neighborhood",
            "prefecture",
            "city",
            "locality",
            "oaza",
            "chome",
            // "poi",
          ],
          // poi_category: [
          //   // "beach",
          //   // "forest",
          //   "island",
          //   // "lake",
          //   // "mountain",
          //   // "nature_reserve",
          // ],
          // poi_category_exclusions: [
          //   "restaurant",
          //   "shopping",
          //   "airport",
          //   "sports",
          //   "embassy",
          //   "hotel",
          //   "education",
          //   "government",
          //   "lodging",
          //   "entertainment",
          //   "school",
          //   "transportation",
          //   "services",
          //   "travel_agent",
          // ],
        }}
      />
    </div>
  );
}
